import {useState, useCallback} from 'react';
import {documentApi} from '../../api/document/api';
import {downloadBlobWithChromeIosSupport, getFilenameFromContentDisposition} from '@utils/downloadFile';

interface UseDownloadDocumentOptions {
    transactionId: string;
    flatten?: boolean;
}

interface UseDownloadDocumentResult {
    isDownloading: boolean;
    downloadPackage: (fallbackFilename?: string) => Promise<void>;
    downloadSignedDocument: (documentId: string, fallbackFilename?: string) => Promise<void>;
    downloadOriginalDocument: (documentId: string, fallbackFilename?: string) => Promise<void>;
    error: Error | null;
}

export function useDownloadDocument({
    transactionId,
    flatten = false
}: UseDownloadDocumentOptions): UseDownloadDocumentResult {
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const downloadPackage = useCallback(async (fallbackFilename = 'package.zip') => {
        try {
            setIsDownloading(true);
            setError(null);

            const response = await documentApi.getDocumentPackage(
                transactionId,
                {flatten} as any
            );

            if (response.data instanceof Blob) {
                const filenameFromHeader = getFilenameFromContentDisposition(response.headers?.['content-disposition']);
                const filename = filenameFromHeader || fallbackFilename;
                downloadBlobWithChromeIosSupport(response.data, filename);
            } else {
                throw new Error('Invalid response: expected Blob');
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to download package');
            setError(error);
            throw error;
        } finally {
            setIsDownloading(false);
        }
    }, [transactionId, flatten]);

    const downloadSignedDocument = useCallback(async (documentId: string, fallbackFilename?: string) => {
        try {
            setIsDownloading(true);
            setError(null);

            const response = await documentApi.getSignedDocument(
                transactionId,
                documentId,
                {flatten} as any
            );

            if (response.data instanceof Blob) {
                const filenameFromHeader = getFilenameFromContentDisposition(response.headers?.['content-disposition']);
                const filename = filenameFromHeader || fallbackFilename || `document-${documentId}.pdf`;
                downloadBlobWithChromeIosSupport(response.data, filename);
            } else {
                throw new Error('Invalid response: expected Blob');
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to download signed document');
            setError(error);
            throw error;
        } finally {
            setIsDownloading(false);
        }
    }, [transactionId, flatten]);

    const downloadOriginalDocument = useCallback(async (documentId: string, fallbackFilename?: string) => {
        try {
            setIsDownloading(true);
            setError(null);

            const response = await documentApi.getOriginalDocument(
                transactionId,
                documentId
            );

            if (response.data instanceof Blob) {
                const filenameFromHeader = getFilenameFromContentDisposition(response.headers?.['content-disposition']);
                const filename = filenameFromHeader || fallbackFilename || `document-${documentId}-original.pdf`;
                downloadBlobWithChromeIosSupport(response.data, filename);
            } else {
                throw new Error('Invalid response: expected Blob');
            }
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to download original document');
            setError(error);
            throw error;
        } finally {
            setIsDownloading(false);
        }
    }, [transactionId]);

    return {
        isDownloading,
        downloadPackage,
        downloadSignedDocument,
        downloadOriginalDocument,
        error
    };
}
